import { Card, CardContent } from "@/components/ui/card";
import type { ActivityAnalytics, AdminUserSummary } from "@/lib/api";

import { ActivityChart } from "./activity-chart";
import { ActivityExplorer } from "./activity-explorer";

interface Props {
  analytics: ActivityAnalytics;
  users: AdminUserSummary[];
  actions: string[];
}

export function ActivityDashboard({ analytics, users, actions }: Props) {
  const { summary, timeseries, breakdown } = analytics;

  const stats = [
    { label: "Events", value: summary.total_events },
    { label: "Logins", value: summary.total_logins },
    { label: "Active users", value: summary.active_users },
    { label: "Signups", value: summary.signups },
  ];

  return (
    <div className="space-y-8">
      <section>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {stats.map((stat) => (
            <Card key={stat.label}>
              <CardContent className="py-4">
                <p className="text-muted text-xs">{stat.label}</p>
                <p className="text-primary text-2xl font-bold">{stat.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
        <p className="text-muted mt-2 text-xs">Last {summary.days} days</p>
      </section>

      <section>
        <h2 className="font-heading text-primary mb-3 text-sm font-bold">Activity over time</h2>
        <Card>
          <CardContent className="py-6">
            <ActivityChart data={timeseries} />
            <div className="text-muted mt-3 flex items-center justify-between text-xs">
              <span>{timeseries[0]?.day}</span>
              <span className="flex items-center gap-3">
                <span className="flex items-center gap-1">
                  <span className="bg-accent inline-block h-2 w-2 rounded-full" /> logins
                </span>
                <span className="flex items-center gap-1">
                  <span className="bg-accent/20 inline-block h-2 w-2 rounded-full" /> all events
                </span>
              </span>
              <span>{timeseries[timeseries.length - 1]?.day}</span>
            </div>
          </CardContent>
        </Card>
      </section>

      {breakdown.length > 0 && (
        <section>
          <h2 className="font-heading text-primary mb-3 text-sm font-bold">By action</h2>
          <Card>
            <CardContent className="space-y-2 py-4">
              {breakdown.map((item) => (
                <div key={item.action} className="flex items-center justify-between text-sm">
                  <span className="font-medium">{item.action}</span>
                  <span className="text-muted">{item.count}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </section>
      )}

      <section>
        <h2 className="font-heading text-primary mb-3 text-sm font-bold">Recent activity</h2>
        <ActivityExplorer users={users} actions={actions} />
      </section>
    </div>
  );
}
